/**
 * 旅のしおり: 日ごとのストーリー
 *
 * 1 日分の訪問スポットを時系列に並べ、その場で撮った写真とメモを添えて表示する。
 */

import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { Camera, Check, Image as ImageIcon, Loader2, MapPin, MapPinPlus, Route, Stamp, StickyNote } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import type { ImmichAsset } from '@/api/services/immich'
import { createVisitFromLocation } from '@/api/services/overland'
import type { TripDay, TripPlanSpot } from '@/api/types/trip'
import { Button } from '@/components/ui/button'
import { formatTimeJST } from '@/lib/activity-format-utils'
import { cn } from '@/lib/utils'
import { ImmichThumbnail } from '@/pages/activity/components/activity-card/immich-thumbnail'
import { PhotoLightbox } from '@/pages/activity/components/activity-card/photo-lightbox'

import { tripPlaceName } from './trip-utils'

type Props = {
  day: TripDay
  dayNumber: number
  /** 訪問を作った後に呼ばれる。しおりを取り直すために使う */
  onVisitCreated?: () => void | Promise<void>
  coverAssetId?: string | null
  onSetCover?: (assetId: string) => void | Promise<void>
  className?: string
}

/** 横スクロールの写真サムネイル列 */
const PhotoStrip = ({ assets, onOpen }: { assets: ImmichAsset[]; onOpen: (index: number) => void }) => (
  <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
    {assets.map((asset, i) => (
      <ImmichThumbnail
        key={asset.id}
        assetId={asset.id}
        alt={asset.originalFileName}
        size="thumbnail"
        onClick={() => onOpen(i)}
        className="size-20 shrink-0 overflow-hidden rounded-md"
      />
    ))}
  </div>
)

export const TripDayTimeline = ({ day, dayNumber, onVisitCreated, coverAssetId, onSetCover, className }: Props) => {
  const [lightboxAssets, setLightboxAssets] = useState<ImmichAsset[] | null>(null)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [creatingPlaceId, setCreatingPlaceId] = useState<number | null>(null)

  const openLightbox = (assets: ImmichAsset[], index: number) => {
    setLightboxAssets(assets)
    setLightboxIndex(index)
  }

  // スタンプを押した時刻を手がかりに、実際の滞在時間は位置ログから割り出させる
  const handleCreateVisit = async (spot: TripPlanSpot) => {
    if (!spot.stamped_at) return
    setCreatingPlaceId(spot.place_id)
    try {
      const visit = await createVisitFromLocation({
        latitude: spot.latitude,
        longitude: spot.longitude,
        timestamp: spot.stamped_at,
        place_id: spot.place_id,
        estimate_arrival: true,
      })
      const stay = visit.arrival_date
        ? `${formatTimeJST(visit.arrival_date)}〜${visit.departure_date ? formatTimeJST(visit.departure_date) : ''}`
        : ''
      toast.success(`「${spot.name}」を訪問として記録しました${stay ? ` (${stay})` : ''}`)
      await onVisitCreated?.()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '訪問の作成に失敗しました')
    } finally {
      setCreatingPlaceId(null)
    }
  }

  const isEmpty =
    day.visits.length === 0 && day.photos.length === 0 && day.notes.length === 0 && day.plan_spots.length === 0

  // 動画はカバーのサムネイルに向かないので画像だけ
  const renderCoverAction = (asset: ImmichAsset) => {
    if (!onSetCover || asset.type !== 'IMAGE') return null
    const isCover = asset.id === coverAssetId
    return (
      <button
        type="button"
        disabled={isCover}
        onClick={() => void onSetCover(asset.id)}
        className="flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-2 text-sm text-white hover:bg-black/80 active:bg-black/90 disabled:opacity-80"
      >
        {isCover ? <Check className="size-4" /> : <ImageIcon className="size-4" />}
        {isCover ? 'カバー写真' : 'カバーに設定'}
      </button>
    )
  }

  return (
    <section className={cn('space-y-3', className)}>
      <div className="flex items-baseline gap-2">
        <span className="rounded-md bg-orange-500 px-2 py-0.5 text-xs font-bold text-white">DAY {dayNumber}</span>
        {/* 日付のみの文字列は UTC 深夜として解釈されるので、ローカル時刻扱いになる形にしてから整形する */}
        <h3 className="text-sm font-semibold">
          {format(new Date(`${day.date}T00:00:00`), 'M月d日(E)', { locale: ja })}
        </h3>
      </div>

      {isEmpty && <p className="pl-4 text-xs text-muted-foreground">記録がありません</p>}

      {/* 左側に縦線を引いて時系列を示す */}
      <ol className="ml-2 space-y-4 border-l border-dashed pl-4">
        {day.visits.map((visit) => (
          <li key={visit.id} className="relative">
            <span className="absolute -left-5.25 top-1.5 size-2.5 rounded-full bg-orange-500 ring-2 ring-background" />
            <div className="flex items-baseline gap-2">
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {formatTimeJST(visit.arrival_date ?? visit.timestamp)}
              </span>
              <p className="min-w-0 flex-1 truncate text-sm font-medium">{tripPlaceName(visit)}</p>
              {visit.has_stamp && <Stamp className="size-3.5 shrink-0 self-center text-orange-500" />}
            </div>
            {visit.place?.city && (
              <p className="text-xs text-muted-foreground">
                {visit.place.prefecture}
                {visit.place.city}
              </p>
            )}
            {visit.photos.length > 0 && (
              <div className="mt-2">
                <PhotoStrip assets={visit.photos} onOpen={(i) => openLightbox(visit.photos, i)} />
              </div>
            )}
          </li>
        ))}

        {day.photos.length > 0 && (
          <li className="relative">
            <span className="absolute -left-5.25 top-1.5 size-2.5 rounded-full bg-sky-500 ring-2 ring-background" />
            <p className="mb-2 flex items-center gap-1 text-xs text-muted-foreground">
              <Camera className="size-3.5" />
              移動中の写真 {day.photos.length} 枚
            </p>
            <PhotoStrip assets={day.photos} onOpen={(i) => openLightbox(day.photos, i)} />
          </li>
        )}

        {day.notes.map((note) => (
          <li key={note.id} className="relative">
            <span className="absolute -left-5.25 top-1.5 size-2.5 rounded-full bg-amber-500 ring-2 ring-background" />
            <p className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">
              <StickyNote className="size-3.5" />
              {formatTimeJST(note.timestamp)}
            </p>
            <p className="wrap-break-word rounded-md bg-muted p-2 text-sm whitespace-pre-wrap">{note.content}</p>
          </li>
        ))}

        {day.plan_spots.length > 0 && (
          <li className="relative">
            <span className="absolute -left-5.25 top-1.5 size-2.5 rounded-full bg-muted-foreground/40 ring-2 ring-background" />
            <p className="mb-1.5 flex items-center gap-1 text-xs text-muted-foreground">
              <Route className="size-3.5" />
              予定していた場所 {day.plan_spots.length} 件
            </p>
            <ul className="space-y-1">
              {day.plan_spots.map((spot) => (
                <li key={`${spot.plan_id}-${spot.place_id}`} className="space-y-1">
                  <div className="flex items-baseline gap-2">
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {spot.arrival_at ? formatTimeJST(spot.arrival_at) : '--:--'}
                    </span>
                    <p className="min-w-0 flex-1 truncate text-sm text-muted-foreground">{spot.name}</p>
                  </div>
                  {/* スタンプがあるのに訪問が無い＝GPS が拾えていないだけ。手動で足せるようにする */}
                  {spot.stamped_at && (
                    <div className="flex flex-wrap items-center gap-2 pl-11">
                      <span className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400">
                        <Stamp className="size-3.5 shrink-0" />
                        スタンプ済み・訪問の記録がありません
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1 px-2! text-xs"
                        disabled={creatingPlaceId !== null}
                        onClick={() => void handleCreateVisit(spot)}
                      >
                        {creatingPlaceId === spot.place_id ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <MapPinPlus className="size-3.5" />
                        )}
                        訪問を作る
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </li>
        )}
      </ol>

      {day.visits.length === 0 && day.photos.length > 0 && (
        <p className="flex items-center gap-1 pl-4 text-xs text-muted-foreground">
          <MapPin className="size-3.5" />
          スポットの記録はありません
        </p>
      )}

      <PhotoLightbox
        assets={lightboxAssets ?? []}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        open={lightboxAssets !== null}
        onOpenChange={(open) => !open && setLightboxAssets(null)}
        renderActions={onSetCover ? renderCoverAction : undefined}
      />
    </section>
  )
}
